import React, { useState } from 'react';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip'; // To display status
import Dialog from '@mui/material/Dialog'; // Import Dialog components
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField'; // Import TextField for OTP input
import CircularProgress from '@mui/material/CircularProgress'; // For loading states
import Alert from '@mui/material/Alert'; // For error messages

// Function to determine chip color based on status
const getStatusColor = (status) => {
  if (status === 'Pendiente Autorización') return 'warning';
  if (status === 'Autorizada') return 'success';
  if (status === 'Rechazada') return 'error'; // Add color for denied
  return 'default';
};

function OrderCard({ order }) {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [actionToConfirm, setActionToConfirm] = useState(null); // 'authorize' or 'deny'

  // State for OTP Dialog
  const [openOtpDialog, setOpenOtpDialog] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');

  const handleAuthorizeClick = () => {
    setActionToConfirm('authorize');
    setOpenConfirmDialog(true);
  };

  const handleDenyClick = () => {
    setActionToConfirm('deny');
    setOpenConfirmDialog(true);
  };

  const handleViewPdfClick = () => {
    // TODO: Implement PDF viewing/download logic
    console.log('View PDF clicked for:', order.id, order.pdfUrl);
    // window.open(order.pdfUrl, '_blank'); // Example: Open in new tab (if URL is direct)
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setActionToConfirm(null);
  };

  const handleCloseOtpDialog = () => {
    setOpenOtpDialog(false);
    setOtpValue('');
    setOtpError('');
    setIsOtpLoading(false);
  };

  const handleConfirmAction = () => {
    const currentAction = actionToConfirm;
    handleCloseConfirmDialog(); // Close the first dialog

    if (currentAction === 'authorize') {
      // Open OTP dialog instead of finishing
      console.log('Simulating sending OTP to user for order:', order.id);
      setOpenOtpDialog(true);
    } else if (currentAction === 'deny') {
      // TODO: Implement actual deny logic (API call, update state, etc.)
      console.log('CONFIRMED Deny for:', order.id);
      // Potentially update status locally or trigger refresh
    }
  };

  // Simulate OTP Validation
  const handleOtpSubmit = async () => {
     setOtpError('');
     setIsOtpLoading(true);
     console.log(`Simulating OTP validation for order ${order.id} with OTP: ${otpValue}`);

     // --- Simulate Backend OTP Check ---
     await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
     const MOCK_VALID_OTP = '123456'; // Example OTP
     const isOtpValid = otpValue === MOCK_VALID_OTP;
     // --------------------------------

     setIsOtpLoading(false);
     if (isOtpValid) {
         console.log(`OTP CORRECT for ${order.id}. Order Authorized. Simulating PDF/Hash email and status update.`);
         // TODO: Implement actual logic after successful OTP validation (API calls)
         handleCloseOtpDialog();
         // TODO: Update order status in parent component state or trigger refresh
     } else {
         setOtpError('Clave OTP inválida.');
     }
  };

  // Dynamic text for the first confirmation dialog
  const confirmDialogText = actionToConfirm === 'authorize'
    ? `¿Estás seguro de que deseas AUTORIZAR la orden ${order.id}?`
    : `¿Estás seguro de que deseas DENEGAR la orden ${order.id}?`;

  return (
    <>
      <Card sx={{ minWidth: 275, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
            {order.id} - {order.date}
          </Typography>
          <Typography variant="h5" component="div">
            {order.projectName || 'Nombre Proyecto'} {/* Fallback if no name */}
          </Typography>
          <Chip 
            label={order.status}
            color={getStatusColor(order.status)}
            size="small"
            sx={{ mt: 1, mb: 1 }}
          />
          <Typography variant="body2">
            ClickUp ID: {order.clickupId}
            <br />
            {/* Add more details if needed */}
          </Typography>
           <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all', mt: 1 }} color="text.secondary">
            Hash: {order.pdfHash}
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: 'space-between' }}>
          <Button size="small" onClick={handleViewPdfClick} disabled={!order.pdfUrl}>
            Ver PDF
          </Button>
          {order.status === 'Pendiente Autorización' && (
             <Box>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={handleDenyClick}
                    sx={{ mr: 1 }}
                  >
                    Denegar
                  </Button>
                   <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    onClick={handleAuthorizeClick}
                   >
                    Autorizar
                   </Button>
              </Box>
          )}
        </CardActions>
      </Card>

      {/* First Confirmation Dialog (Authorize/Deny) */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {"Confirmar Acción"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {confirmDialogText}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Cancelar</Button>
          <Button onClick={handleConfirmAction} variant="contained" color={actionToConfirm === 'authorize' ? 'secondary' : 'error'} autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Second Dialog (OTP Input) */}
      <Dialog open={openOtpDialog} onClose={handleCloseOtpDialog}>
        <DialogTitle>Verificación OTP</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Introduce la clave OTP enviada a tu correo para confirmar la autorización de la orden {order.id}.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }} color="text.secondary">
            Al completar la autorización, recibirás en tu correo electrónico registrado una confirmación 
            con los detalles de la orden y los códigos hash de seguridad generados.
          </DialogContentText>
          {otpError && <Alert severity="error" sx={{ mb: 2 }}>{otpError}</Alert>}
          <TextField
            autoFocus
            margin="dense"
            id="otp"
            label="Clave OTP"
            type="text" // Consider type="password" or adjust based on OTP format
            fullWidth
            variant="standard"
            value={otpValue}
            onChange={(e) => setOtpValue(e.target.value)}
            disabled={isOtpLoading}
          />
        </DialogContent>
        <DialogActions sx={{ position: 'relative' }}> {/* Relative for progress positioning */}
          <Button onClick={handleCloseOtpDialog} disabled={isOtpLoading}>Cancelar</Button>
          <Button onClick={handleOtpSubmit} variant="contained" disabled={isOtpLoading || !otpValue}>
            Validar OTP
          </Button>
           {isOtpLoading && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  right: 16, // Position near the submit button
                  bottom: 12, // Adjust as needed
                }}
              />
            )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default OrderCard; 